// DropdownMenu.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react'
import './DropdownMenu.css'
import { Button, useTheme } from '@mui/material'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import SettingsIcon from '@mui/icons-material/Settings'
import { dropDownMenuKeys } from '../constants'
import { active } from '../../features/lms/models'
import { inactiveText } from '../../features/Radiology/Constants/constants'
import useCustomStyles from '../../utils/hooks/CustomStylesHook'
import KeycloakService from '../../utils/keycloakService'
import { ADMIN_EXPORT_DATA } from '../../utils/roles'

const styles = (theme: any) => ({
  item: {
    fontFamily: 'Noto Sans',
    fontSize: '12px',
  },
  isSelected: {
    backgroundColor: '#d0e6ff',
  },
  shouldHighlight: {
    backgroundColor: '#e0e0e0',
  },
  isActive: {
    backgroundColor: '#d0e6ff', // Yellow for active options
  },
  isTransParent: {
    backgroundColor: 'transparent',
  },
  menu: {
    fontFamily: 'Noto Sans',
    fontSize: '12px',
  },
  customRender: {
    cursor: 'pointer',
  },
  filterDropdown: {
    position: 'relative',
    display: 'flex',
    gap: theme.spacing(1),
  },
  dropdownWrapper: {
    position: 'relative',
  },
  buttonBase: {
    textTransform: 'none',
    fontSize: '13px',
    fontFamily: 'Noto Sans',
    backgroundColor: 'transparent',
    color: 'inherit',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
    },
  },
  filterActive: {
    '&:hover': {
      backgroundColor: '#1565c0',
      color: '#fff',
    },
  },
  filtersActive: {
    backgroundColor: '#1565c0',
    color: '#fff',
  },
  actionActive: {
    // color: '#fff',
    '&:hover': {
      backgroundColor: '#1565c0',
      color: '#fff',
    },
  },
  redDot: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 8,
    height: 8,
    backgroundColor: 'red',
    borderRadius: '50%',
  },
  dropdownContent: {
    position: 'absolute',
    top: '100%',
    left: 0,
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: 4,
    boxShadow: theme.shadows[3],
    zIndex: 1000,
    minWidth: 160,
    marginTop: theme.spacing(0.5),
  },
  menuItem: {
    fontFamily: 'Noto Sans',
    fontSize: '12px',
    padding: theme.spacing(0.5, 1),
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&.is-active': {
      backgroundColor: '#e0e0e0',
    },
    '&.selected-option': {
      backgroundColor: '#d0e6ff',
    },
    '&.disabled-option': {
      opacity: 0.5,
      pointerEvents: 'none',
    },
  },
  menuDivider: {
    height: 1,
    backgroundColor: '#eee',
    margin: theme.spacing(0.25, 0),
  },
  activeSubmenu: {
    position: 'absolute',
    top: 0,
    left: '100%',
    backgroundColor: '#fff',
    boxShadow: theme.shadows[3],
    borderRadius: 4,
    padding: theme.spacing(0.5, 0),
    zIndex: 1001,
  },
  filterAltIcon: {
    fontSize: '12px',
    mr: 0.5,
  },
})
export type DropdownOption = {
  label: string
  value: string
  subOptions?: DropdownOption[]
  customRender?: React.ReactNode
  disabled?: boolean
  active?: boolean
  resetGroup?: string
}

type DropdownMenuProps = {
  filterOptions?: DropdownOption[]
  actionOptions?: DropdownOption[]
  printOptions?: DropdownOption[]
  onSelect?: (value: string[]) => void
  filterApply?: boolean
  showFilter?: boolean
  showAction?: boolean
  showPrint?: boolean
  selectedId: boolean
  forceDateFilterActive?: boolean
}

type RootKey = 'filter' | 'action' | 'print' | null

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  filterOptions = [],
  actionOptions = [],
  onSelect,
  filterApply,
  showFilter = true,
  showAction = true,
  selectedId,
  forceDateFilterActive = false,
}) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const menuRef = useRef<HTMLDivElement>(null)

  const [openRoot, setOpenRoot] = useState<RootKey>(null)
  const [activePath, setActivePath] = useState<string[]>([])
  const [menuEnabled, setMenuEnabled] = useState(false)
  const [selectedPaths, setSelectedPaths] = useState<string[][]>([])
  // const menuRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout>()
  // Utility functions
  const closeMenu = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setOpenRoot(null)
    setActivePath([])
    setMenuEnabled(false)
  }, [])

  const handlePathSelection = useCallback(
    (currentPaths: string[][], path: string[], exists: boolean): string[][] => {
      const lastValue = path?.[path?.length - 1]?.toLowerCase()
      let updated = [...currentPaths]

      if (lastValue === active || lastValue === inactiveText) {
        updated = updated?.filter((p) => {
          const val = p?.[p?.length - 1]?.toLowerCase()
          return val !== active && val !== inactiveText
        })
        if (!exists) updated?.push(path)
      } else if (path?.length > 1) {
        const parentKey = path?.[0]
        updated = updated?.filter((p) => p?.[0] !== parentKey)
        if (!exists) updated?.push(path)
      } else {
        if (exists) {
          updated = updated?.filter((p) => p?.join('/') !== path?.join('/'))
        } else {
          updated?.push(path)
        }
      }

      return updated
    },
    []
  )

  const handleSelect = useCallback(
    (path: string[]) => {
      onSelect?.(path)
      if (openRoot !== dropDownMenuKeys?.action) {
        setSelectedPaths((prev) => {
          const exists = prev?.some((p) => p?.join('/') === path?.join('/'))

          // If selecting a parent with children, deselect all child paths
          const hasChildren = filterOptions?.some(
            (opt) =>
              opt?.value === path?.[0] &&
              (opt?.subOptions?.length || opt?.customRender)
          )

          if (hasChildren && path?.length === 1 && !exists) {
            // Remove all children of this parent
            const updated = prev?.filter((p) => p?.[0] !== path?.[0])
            updated?.push(path)
            return updated
          }

          return handlePathSelection(prev, path, exists)
        })
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(closeMenu, 100)
    },
    [onSelect, openRoot, closeMenu, handlePathSelection, filterOptions]
  )
  const toggleRoot = useCallback((root: Exclude<RootKey, null>) => {
    setOpenRoot((prev) => {
      const newRoot = prev === root ? null : root
      setActivePath([])
      setMenuEnabled(!!newRoot)
      return newRoot
    })
  }, [])

  const handleEnterItem = useCallback(
    (value: string, level: number) => {
      if (!menuEnabled) return
      setActivePath((prev) => {
        const next = [...prev]
        next[level] = value
        return next?.slice(0, level + 1)
      })
    },
    [menuEnabled]
  )

  // useEffect(() => {
  //   const handleClickOutside = (e: MouseEvent) => {
  //     if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
  //       closeMenu()
  //     }
  //   }
  //   document.addEventListener('mousedown', handleClickOutside)
  //   return () => document.removeEventListener('mousedown', handleClickOutside)
  // }, [closeMenu])
  // useEffect(() => {
  //   return () => {
  //     if (timeoutRef.current) {
  //       clearTimeout(timeoutRef.current)
  //     }
  //   }
  // }, [])
  // ...existing code...

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeMenu()
      }
    },
    [closeMenu, menuRef]
  )

  const clearMenuTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [handleClickOutside])

  useEffect(() => {
    return () => {
      clearMenuTimeout()
    }
  }, [clearMenuTimeout])

  // ...existing code...
  // Render helpers
  const hasSelectedChild = useCallback(
    (opt: DropdownOption, parentPath: string[] = []): boolean => {
      const path = [...parentPath, opt.value]
      return (
        selectedPaths.some((p) => p.join('/') === path.join('/')) ||
        opt.subOptions?.some((child) => hasSelectedChild(child, path)) ||
        false
      )
    },
    [selectedPaths]
  )
  const handleCustomRenderClick = useCallback(
    (e: React.MouseEvent, path: string[]) => {
      e.stopPropagation()
      setSelectedPaths((prev) => {
        const exists = prev?.some(
          (p) => p?.length === path?.length && p?.every((v, i) => v === path[i])
        )
        let updated = exists
          ? prev?.filter(
              (p) =>
                !(
                  p?.length === path?.length &&
                  p?.every((v, i) => v === path[i])
                )
            )
          : [...prev, path]

        // If parent is selected, remove all children
        if (!exists && path?.length === 1) {
          updated = updated?.filter(
            (p) => p?.[0] !== path?.[0] || p?.length === 1
          )
        }

        if (openRoot === dropDownMenuKeys?.filter) {
          if (path?.length > 1) {
            const map = new Map<string, string[]>()
            for (const item of updated) {
              if (item?.length > 1) {
                map?.set(item?.[0], item)
              } else {
                map?.set(item?.join('/'), item)
              }
            }
            updated = Array.from(map?.values())
          }

          const lastValue = path?.[path?.length - 1]?.toLowerCase()
          if (lastValue === active || lastValue === inactiveText) {
            updated = updated?.filter((p) => {
              const val = p?.[p?.length - 1]?.toLowerCase()
              if (lastValue === active) return val !== inactiveText
              if (lastValue === inactiveText) return val !== active
              return true
            })
          }
        }
        return updated
      })
    },
    [openRoot]
  )
  const renderOption = useCallback(
    (opt: DropdownOption, level = 0, parentPath: string[] = []) => {
      const path = [...parentPath, opt?.value]
      const isActive = activePath[level] === opt?.value
      const hasChildren = !!opt?.subOptions?.length || !!opt?.customRender
      const isSelected = selectedPaths?.some(
        (p) => p?.length === path?.length && p?.every((v, i) => v === path?.[i])
      )
      const isMainParentSelected = level === 0
      const childSelected = hasChildren && hasSelectedChild(opt, parentPath)
      const isOptionActive = opt?.active === true
      const getBackgroundClass = () => {
        if (isSelected) {
          return classes?.isSelected // Selected takes priority
        } else if (isOptionActive) {
          return classes?.isActive // Active option gets yellow background
        } else if (childSelected) {
          return classes?.shouldHighlight // Parent with selected children
        } else if (isActive) {
          return classes?.shouldHighlight // Hovered item
        } else {
          return classes?.isTransParent // No styling
        }
      }

      const getClassName = () => {
        const baseClass = `${getBackgroundClass()} menu-item level-${level}`
        const activeClass = isActive ? 'is-active' : ''
        const selectedClass = isSelected ? 'selected-option' : ''
        const optionActiveClass = isOptionActive ? 'active-option' : ''
        const childSelectedClass = childSelected ? 'has-selected-child' : ''
        const disabledClass = opt?.disabled ? 'disabled-option' : ''

        return `${baseClass} ${activeClass} ${selectedClass} ${optionActiveClass} ${childSelectedClass} ${disabledClass}`.trim()
      }

      return (
        <div
          key={`${level}-${opt.value}`}
          className={getClassName()}
          onMouseEnter={() => {
            if (!opt?.disabled) handleEnterItem(opt?.value, level)
          }}
          aria-disabled={opt?.disabled || !selectedId}
          data-disabled={opt?.disabled || !selectedId}
          data-active={isOptionActive}
          title={isOptionActive ? `${opt?.label} (Active)` : opt?.label}
        >
          <div
            className={`${classes?.menu} menu-label`}
            onClick={() => {
              if (!opt?.disabled && !hasChildren) {
                handleSelect(path)
              } else if (hasChildren && !opt?.disabled) {
                // Allow parent selection
                handleSelect(path)
              }
            }}
          >
            <span
              className={`${classes?.menu} menu-text`}
              aria-disabled={opt?.active && !hasChildren}
            >
              {opt?.label}
            </span>
            {(isSelected || opt?.active) && (
              <span className='checkmark'>✔</span>
            )}
            {hasChildren && <span className='arrow'>▶</span>}
          </div>

          {hasChildren && menuEnabled && isActive && (
            <div className={`active-submenu ${classes?.Menu}`}>
              {opt?.customRender ? (
                <div
                  className={`menu-item level-${level} 
                  ${isSelected ? 'selected-option' : ''} 
                  ${isMainParentSelected ? 'main-parent-selected' : ''}
                  ${classes?.customRender}`}
                  onClick={(e) => handleCustomRenderClick(e, path)}
                >
                  {opt?.customRender}
                </div>
              ) : (
                opt?.subOptions?.map((child, idx) => (
                  <React.Fragment key={child.value}>
                    {renderOption(child, level + 1, path)}
                    {idx < (opt?.subOptions?.length ?? 0) - 1 && (
                      <div className='menu-divider' />
                    )}
                  </React.Fragment>
                ))
              )}
            </div>
          )}
        </div>
      )
    },
    [
      activePath,
      classes,
      handleCustomRenderClick,
      handleEnterItem,
      handleSelect,
      hasSelectedChild,
      menuEnabled,
      selectedId,
      selectedPaths,
    ]
  )
  return (
    <div className='filter-dropdown' ref={menuRef}>
      {showFilter && (
        <div className='dropdown-wrapper'>
          <Button
            size='small'
            className={`${classes?.buttonBase} ${
              openRoot === 'filter' || forceDateFilterActive
                ? classes?.filterActive
                : ''
            } filter-button ${filterApply ? classes?.filtersActive : ''}`}
            disableRipple
            onClick={() => toggleRoot('filter')}
            onMouseEnter={() => {
              if (openRoot) toggleRoot('filter')
            }}
          >
            <div className={classes?.dropdownWrapper}>
              <FilterAltIcon className={classes?.filterAltIcon} />
            </div>
            Filter
          </Button>

          {openRoot === dropDownMenuKeys?.filter && !!filterOptions?.length && (
            <div className='dropdown-content'>
              {filterOptions?.map((opt, idx) => (
                <React.Fragment key={opt?.value}>
                  {renderOption(opt, 0)}
                  {idx < filterOptions?.length - 1 && (
                    <div className='menu-divider' />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}

      {showAction && KeycloakService.hasRole([ADMIN_EXPORT_DATA]) && (
        <div className='dropdown-wrapper'>
          <Button
            size='small'
            className={`filter-button ${classes?.buttonBase} ${
              openRoot === 'action' ? classes?.actionActive : ''
            }`}
            disableRipple
            onClick={() => toggleRoot('action')}
            onMouseEnter={() => {
              if (openRoot) toggleRoot('action')
            }}
          >
            <SettingsIcon className={classes?.filterAltIcon} />
            Action
          </Button>

          {openRoot === dropDownMenuKeys?.action && !!actionOptions?.length && (
            <div className='dropdown-content'>
              {actionOptions?.map((opt, idx) => (
                <React.Fragment key={opt?.value}>
                  {renderOption(opt, 0)}
                  {idx < actionOptions?.length - 1 && (
                    <div className='menu-divider' />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DropdownMenu
